import React, { useMemo } from "react";
import { useField } from "formik";
import {
    RuleGroupType,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useOverlayContainer } from "@design-system/overlay/OverlayContainerContext";
import {
    FormRuleConditionType,
    TransactionRuleConditionFormSchema,
} from "../../TransactionRuleForm/constants";
import styles from "../TransferRuleForm.module.scss";
import { AccountConditionForm } from "../../TransactionRuleForm/RuleConditionsForm/AccountConditionForm";
import { BankDescriptionConditionForm } from "../../TransactionRuleForm/RuleConditionsForm/BankDescriptionConditionForm";
import { CategoryConditionForm } from "../../TransactionRuleForm/RuleConditionsForm/CategoryConditionForm";
import { CounterpartyConditionForm } from "../../TransactionRuleForm/RuleConditionsForm/CounterpartyConditionForm";
import { DateConditionForm } from "../../TransactionRuleForm/RuleConditionsForm/DateConditionForm";
import { TransferRuleEntityConditionForm } from "./TransferRuleEntityConditionForm";
import { AmountConditionForm } from "./AmountConditionForm";

const TRANSFER_RULE_SUPPORTED_CONDITION_TYPES = [
    FormRuleConditionType.CATEGORY,
    FormRuleConditionType.AMOUNT,
    FormRuleConditionType.BANK_DESCRIPTION,
    FormRuleConditionType.ACCOUNT,
    FormRuleConditionType.COUNTERPARTY,
    FormRuleConditionType.DATE,
];

interface Props {
    condition: TransactionRuleConditionFormSchema;
    index: number;
    onRemove: (id: string) => void;
    selectedConditionTypes: FormRuleConditionType[];
    transferDirection: TransferRuleDirection;
}

export const TransferRuleConditionForm: React.FC<Props> = ({
    condition,
    index,
    onRemove,
    selectedConditionTypes,
    transferDirection,
}) => {
    const { enabled: isGlFirst } = useIsGlFirst();
    const { containerRef } = useOverlayContainer();
    const [conditionTypeField, , conditionTypeHelpers] = useField<
        TransactionRuleConditionFormSchema["conditionType"]
    >(`conditions.${transferDirection}.${index}.conditionType`);

    const canRemove =
        condition.conditionType &&
        ![
            FormRuleConditionType.ENTITY,
            FormRuleConditionType.DATE_PROXIMITY,
        ].includes(condition.conditionType);

    const conditionType = conditionTypeField.value;

    const conditionTypesOptions = useMemo(
        () =>
            Object.values(FormRuleConditionType)
                .filter(
                    (type) =>
                        (TRANSFER_RULE_SUPPORTED_CONDITION_TYPES.includes(
                            type,
                        ) &&
                            !selectedConditionTypes.includes(type)) ||
                        type === condition.conditionType,
                )
                .filter(
                    // GL-first workspaces classify by general ledger, so
                    // category conditions are never selectable as a new
                    // option. Keep the category option when it's already
                    // the condition's selected type so pre-existing rules
                    // remain visible and editable.
                    (type) =>
                        !(
                            isGlFirst &&
                            type === FormRuleConditionType.CATEGORY &&
                            condition.conditionType !==
                                FormRuleConditionType.CATEGORY
                        ),
                )
                .map((value) => {
                    if (value === FormRuleConditionType.COUNTERPARTY) {
                        return {
                            value,
                            label: "Counterparty",
                        };
                    }

                    return {
                        value,
                        label: value,
                    };
                }),
        [condition.conditionType, selectedConditionTypes, isGlFirst],
    );

    return (
        <>
            <div className={styles.sectionRowContent}>
                {(!conditionType ||
                    ![FormRuleConditionType.ENTITY].includes(
                        conditionType,
                    )) && (
                    <Picker.Select
                        items={conditionTypesOptions}
                        value={conditionType ?? null}
                        onValueChange={(v) => {
                            if (v !== null) {
                                conditionTypeHelpers.setValue(v);
                            }
                        }}
                        placeholder="Select"
                    />
                )}
                {conditionType === FormRuleConditionType.CATEGORY && (
                    <CategoryConditionForm
                        index={index}
                        transferDirection={transferDirection}
                    />
                )}
                {conditionType === FormRuleConditionType.SUGGESTED_CATEGORY && (
                    <CategoryConditionForm
                        index={index}
                        transferDirection={transferDirection}
                    />
                )}
                {conditionType === FormRuleConditionType.BANK_DESCRIPTION && (
                    <BankDescriptionConditionForm
                        index={index}
                        transferDirection={transferDirection}
                    />
                )}
                {conditionType === FormRuleConditionType.AMOUNT && (
                    <AmountConditionForm
                        index={index}
                        transferDirection={transferDirection}
                    />
                )}
                {conditionType === FormRuleConditionType.ACCOUNT && (
                    <AccountConditionForm
                        index={index}
                        transferDirection={transferDirection}
                    />
                )}
                {conditionType === FormRuleConditionType.ENTITY && (
                    <TransferRuleEntityConditionForm
                        index={index}
                        groupType={RuleGroupType.TRANSFER}
                        transferDirection={transferDirection}
                    />
                )}
                {conditionType === FormRuleConditionType.COUNTERPARTY && (
                    <CounterpartyConditionForm
                        index={index}
                        transferDirection={transferDirection}
                    />
                )}
                {conditionType === FormRuleConditionType.DATE && (
                    <DateConditionForm
                        index={index}
                        transferDirection={transferDirection}
                        container={containerRef?.current ?? undefined}
                    />
                )}
            </div>

            {canRemove && (
                <Menu.More>
                    <Menu.Item
                        onClick={() => onRemove(condition.id)}
                        destructive
                    >
                        Remove
                    </Menu.Item>
                </Menu.More>
            )}
        </>
    );
};
